/**
 * Browser-native media transport plugin. The workspace talks to this small
 * contract instead of RTCPeerConnection directly, so an SFU adapter can later
 * replace it without changing messaging screens.
 */
export class NativeWebRTCTransport {
  constructor({ iceServers, onIceCandidate, onTrack, onConnectionStateChange, onLocalStreamChange }) {
    this.iceServers = iceServers;
    this.onIceCandidate = onIceCandidate;
    this.onTrack = onTrack;
    this.onConnectionStateChange = onConnectionStateChange;
    this.onLocalStreamChange = onLocalStreamChange;
    this.screenStream = null;
    this.sourceChanging = false;
    this.cameraTrack = null;
    this.peer = null;
    this.localStream = null;
    this.pendingCandidates = [];
    this.facingMode = 'user';
    this.stopped = false;
  }

  async initialize(mediaType) {
    if (!navigator.mediaDevices?.getUserMedia || !window.RTCPeerConnection) {
      throw new Error('Calling is not supported in this browser.');
    }
    let expired = false;
    let timer;
    const media = navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }, video: mediaType === 'video' ? { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 24, max: 30 }, facingMode: { ideal: 'user' } } : false })
      .then((stream) => {
        if (expired || this.stopped) {
          stream.getTracks().forEach((track) => track.stop());
          throw new Error('Media request was cancelled.');
        }
        return stream;
      });
    try {
      this.localStream = await Promise.race([
        media,
        new Promise((_, reject) => {
          timer = setTimeout(() => {
            expired = true;
            reject(new Error(`Permission request timed out. Allow microphone${mediaType === 'video' ? ' and camera' : ''} access, then try again.`));
          }, 12_000);
        }),
      ]);
    } finally {
      clearTimeout(timer);
    }
    this.cameraTrack = this.localStream.getVideoTracks()[0] || null;
    this.peer = new window.RTCPeerConnection({ iceServers: this.iceServers });
    this.localStream.getTracks().forEach((track) => this.peer.addTrack(track, this.localStream));
    this.peer.onicecandidate = ({ candidate }) => candidate && this.onIceCandidate?.(candidate.toJSON?.() || candidate);
    this.peer.ontrack = ({ streams }) => this.onTrack?.(streams[0]);
    this.peer.onconnectionstatechange = () => this.onConnectionStateChange?.(this.peer.connectionState);
    return this.localStream;
  }

  async createOffer({ iceRestart = false } = {}) {
    if (iceRestart) this.peer.restartIce?.();
    const offer = await this.peer.createOffer(iceRestart ? { iceRestart: true } : undefined);
    await this.peer.setLocalDescription(offer);
    return { type: offer.type, sdp: offer.sdp };
  }

  async applyOfferAndCreateAnswer(description) {
    await this.peer.setRemoteDescription(description);
    await this.flushPendingCandidates();
    const answer = await this.peer.createAnswer();
    await this.peer.setLocalDescription(answer);
    return { type: answer.type, sdp: answer.sdp };
  }

  async applyAnswer(description) {
    await this.peer.setRemoteDescription(description);
    await this.flushPendingCandidates();
  }

  async createRecoveryOffer() {
    return this.createOffer({ iceRestart: true });
  }

  async addIceCandidate(candidate) {
    if (!candidate || !this.peer) return;
    if (!this.peer.remoteDescription) {
      this.pendingCandidates.push(candidate);
      return;
    }
    await this.peer.addIceCandidate(candidate);
  }

  async flushPendingCandidates() {
    if (!this.peer?.remoteDescription) return;
    const pending = this.pendingCandidates.splice(0);
    for (const candidate of pending) await this.peer.addIceCandidate(candidate);
  }

  toggleMute() {
    const tracks = this.localStream?.getAudioTracks() || [];
    const shouldDisable = tracks.some((track) => track.enabled);
    tracks.forEach((track) => { track.enabled = !shouldDisable; });
    return shouldDisable;
  }

  toggleCamera() {
    const tracks = this.cameraTrack ? [this.cameraTrack] : (this.localStream?.getVideoTracks() || []);
    const shouldDisable = tracks.some((track) => track.enabled);
    tracks.forEach((track) => { track.enabled = !shouldDisable; });
    return shouldDisable;
  }

  publishLocal() {
    if (this.localStream && typeof MediaStream !== 'undefined') this.localStream = new MediaStream(this.localStream.getTracks());
    this.onLocalStreamChange?.(this.localStream, { facingMode: this.facingMode, sharingScreen: Boolean(this.screenStream), cameraOff: !this.cameraTrack || !this.cameraTrack.enabled || this.cameraTrack.readyState === 'ended' });
  }

  async changeSource(action) {
    if (this.sourceChanging) throw new Error('A video source change is already in progress.');
    if (this.stopped) throw new Error('The call has ended.');
    this.sourceChanging = true;
    try { return await action(); } finally { this.sourceChanging = false; }
  }

  switchCamera() { return this.changeSource(() => this._switchCamera()); }
  startScreenShare() { return this.changeSource(() => this._startScreenShare()); }
  stopScreenShare() { return this.changeSource(() => this._stopScreenShare()); }

  async _switchCamera() {
    if (this.screenStream) throw new Error('Stop screen sharing before switching cameras.');
    const oldTrack = this.localStream?.getVideoTracks?.()[0];
    const sender = this.peer?.getSenders?.().find((item) => item.track?.kind === 'video');
    if (!oldTrack || !sender?.replaceTrack) throw new Error('Camera switching is not supported on this device.');
    const nextFacingMode = this.facingMode === 'user' ? 'environment' : 'user';
    const capture = () => navigator.mediaDevices.getUserMedia({ video: { facingMode: { exact: nextFacingMode }, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false });
    let replacementStream;
    try { replacementStream = await capture(); }
    catch (cause) {
      if (!['NotReadableError', 'AbortError'].includes(cause.name)) throw new Error('No alternate camera is available, or camera access was denied.');
      // Some mobile devices require exclusive camera access. Audio stays live.
      oldTrack.stop();
      try { replacementStream = await capture(); }
      catch { this.publishLocal(); throw new Error('The alternate camera could not be opened. Your microphone is still connected.'); }
    }
    const replacement = replacementStream.getVideoTracks()[0];
    try {
      if (!replacement || this.stopped) throw new Error('Camera switch cancelled or no alternate camera is available.');
      replacement.enabled = oldTrack.enabled;
      await sender.replaceTrack(replacement);
      if (this.stopped) throw new Error('Camera switch cancelled.');
      this.localStream.removeTrack?.(oldTrack); this.localStream.addTrack?.(replacement);
      this.cameraTrack = replacement;
      oldTrack.stop();
      this.facingMode = replacement.getSettings?.().facingMode || nextFacingMode;
      this.publishLocal();
      return this.facingMode;
    } catch (cause) { replacementStream.getTracks().forEach((track) => track.stop()); this.publishLocal(); throw cause; }
  }

  async _startScreenShare() {
    if (this.screenStream) throw new Error('Your screen is already being shared.');
    if (!navigator.mediaDevices?.getDisplayMedia) throw new Error('Screen sharing is unavailable in this browser.');
    const sender = this.peer?.getSenders?.().find((item) => item.track?.kind === 'video');
    if (!sender?.replaceTrack || this.stopped) throw new Error('Connect a video call before sharing your screen.');
    // Called directly from the user's button gesture; permission is requested each time.
    const display = await navigator.mediaDevices.getDisplayMedia({ video: { frameRate: { ideal: 15, max: 30 } }, audio: false });
    const track = display.getVideoTracks()[0];
    try {
      if (!track || this.stopped) throw new Error('Screen sharing was cancelled.');
      await sender.replaceTrack(track);
      if (this.stopped) throw new Error('Screen sharing was cancelled.');
      this.cameraTrack = this.localStream.getVideoTracks()[0];
      this.localStream.removeTrack(this.cameraTrack); this.localStream.addTrack(track);
      this.screenStream = display;
      track.onended = () => this.stopScreenShare().catch(() => {});
      this.publishLocal();
      return true;
    } catch (cause) { display.getTracks().forEach((item) => item.stop()); throw cause; }
  }

  async _stopScreenShare() {
    if (!this.screenStream) return false;
    const display = this.screenStream;
    const sender = this.peer?.getSenders?.().find((item) => item.track?.kind === 'video');
    if (!this.stopped && this.cameraTrack && this.cameraTrack.readyState !== 'ended') {
      await sender?.replaceTrack(this.cameraTrack);
      display.getVideoTracks().forEach((track) => this.localStream?.removeTrack(track));
      this.localStream?.addTrack(this.cameraTrack);
    }
    this.screenStream = null;
    display.getTracks().forEach((track) => { track.onended = null; track.stop(); });
    this.publishLocal();
    return false;
  }

  stop() {
    this.stopped = true;
    this.screenStream?.getTracks().forEach((track) => { track.onended = null; track.stop(); });
    this.screenStream = null;
    this.cameraTrack?.stop();
    this.cameraTrack = null;
    this.peer?.close();
    this.peer = null;
    this.localStream?.getTracks().forEach((track) => track.stop());
    this.localStream = null;
    this.pendingCandidates = [];
  }
}

export default NativeWebRTCTransport;
